import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  Pin, Link2, Unlink, Loader2, Send, Clock, Calendar as CalendarIcon,
  CheckCircle2, XCircle, Trash2, ExternalLink, ChevronDown, AlertTriangle,
  RefreshCw, Sparkles, RotateCcw
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Calendar } from './ui/calendar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth handled via httpOnly cookie — no manual headers needed
const authHeaders = () => ({});

/* ─── Main Scheduler Component ─── */
const PinterestScheduler = ({ pins, pinKits, content }) => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [username, setUsername] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsError, setBoardsError] = useState('');
  const [scheduledPins, setScheduledPins] = useState([]);
  const [publishHistory, setPublishHistory] = useState([]);

  // Publish/Schedule modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('publish');
  const [selectedPinIndex, setSelectedPinIndex] = useState(0);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedBoardName, setSelectedBoardName] = useState('');
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);

  // Smart suggest state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAltText, setEditAltText] = useState('');

  // Fetch connection status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pinterest/status`, authHeaders());
      setConnected(res.data.connected);
      setConfigured(res.data.configured);
      setTokenExpired(res.data.reason === 'token_expired');
      if (res.data.connected) {
        setUsername(res.data.username || '');
      }
    } catch (err) {
      console.error('Pinterest status error:', err);
    }
  }, []);

  const fetchBoards = useCallback(async () => {
    setBoardsLoading(true);
    setBoardsError('');
    try {
      const res = await axios.get(`${API_URL}/api/pinterest/boards`, authHeaders());
      setBoards(res.data.boards || []);
      if (res.data.boards?.length > 0) {
        setSelectedBoard(res.data.boards[0].id);
        setSelectedBoardName(res.data.boards[0].name);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setBoardsError('Token expired — reconnect');
        setTokenExpired(true);
        setConnected(false);
      } else {
        setBoardsError('Could not fetch boards');
      }
    }
    setBoardsLoading(false);
  }, []);

  const fetchScheduled = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pinterest/scheduled`, authHeaders());
      setScheduledPins(res.data.scheduled_pins || []);
    } catch (err) { console.error('Scheduled pins error:', err); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pinterest/history`, authHeaders());
      setPublishHistory(res.data.published_pins || []);
    } catch (err) { console.error('History error:', err); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (connected) {
      fetchBoards();
      fetchScheduled();
      fetchHistory();
    }
  }, [connected, fetchBoards, fetchScheduled, fetchHistory]);

  // Connect Pinterest via OAuth
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API_URL}/api/pinterest/auth`, authHeaders());
      if (res.data.oauth_url) {
        window.location.href = res.data.oauth_url;
        return;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not start Pinterest connection';
      alert(detail);
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API_URL}/api/pinterest/disconnect`, {}, authHeaders());
      setConnected(false);
      setUsername('');
      setBoards([]);
      setScheduledPins([]);
      setPublishHistory([]);
      setTokenExpired(false);
    } catch (err) { console.error('Disconnect error:', err); }
  };

  // ─── Smart Suggest ───
  const fetchSmartSuggest = async (pinIndex) => {
    setAiLoading(true);
    setAiSuggestion(null);
    setAiApplied(false);
    try {
      const kit = pinKits?.[pinIndex];
      const res = await axios.post(`${API_URL}/api/content/pinterest-smart-suggest`, {
        product_title: kit?.title || content?.title || `Pin ${pinIndex + 1}`,
        product_type: content?.product_type || '',
        pin_description: kit?.description || '',
        boards: boards.map(b => ({ id: b.id, name: b.name })),
        country: content?.country || 'United States',
      }, authHeaders());

      setAiSuggestion(res.data);

      // Auto-apply suggestions
      if (res.data.title) setEditTitle(res.data.title);
      if (res.data.description) {
        const desc = res.data.description;
        const tags = res.data.hashtags?.join(' ') || '';
        setEditDescription(tags ? `${desc}\n\n${tags}` : desc);
      }
      if (res.data.alt_text) setEditAltText(res.data.alt_text);

      // Auto-select suggested board
      if (res.data.best_board?.id) {
        const match = boards.find(b => b.id === res.data.best_board.id);
        if (match) {
          setSelectedBoard(match.id);
          setSelectedBoardName(match.name);
        }
      }
      setAiApplied(true);
    } catch (err) {
      console.error('Smart suggest error:', err);
    }
    setAiLoading(false);
  };

  // Open publish/schedule modal
  const openModal = (mode, pinIndex) => {
    const kit = pinKits?.[pinIndex];
    setModalMode(mode);
    setSelectedPinIndex(pinIndex);
    setPublishResult(null);
    setScheduleDate(null);
    setScheduleTime('09:00');
    setAiSuggestion(null);
    setAiApplied(false);
    setEditTitle(kit?.title || content?.title || `Pin ${pinIndex + 1}`);
    setEditDescription(kit?.description || '');
    setEditAltText(kit?.alt_text || '');
    setShowModal(true);
  };

  // Restore original (undo AI)
  const restoreOriginal = () => {
    const kit = pinKits?.[selectedPinIndex];
    setEditTitle(kit?.title || content?.title || '');
    setEditDescription(kit?.description || '');
    setEditAltText(kit?.alt_text || '');
    if (boards.length > 0) {
      setSelectedBoard(boards[0].id);
      setSelectedBoardName(boards[0].name);
    }
    setAiApplied(false);
  };

  // Publish or schedule
  const handleSubmit = async () => {
    if (!pins[selectedPinIndex]) return;
    setPublishing(true);
    setPublishResult(null);

    const pin = pins[selectedPinIndex];

    const payload = {
      pin_image_data: pin.dataUrl,
      title: editTitle.slice(0, 100),
      description: editDescription.slice(0, 500),
      alt_text: (editAltText || editTitle).slice(0, 500),
      board_id: selectedBoard,
      board_name: selectedBoardName,
      link: '',
    };

    try {
      if (modalMode === 'publish') {
        const res = await axios.post(`${API_URL}/api/pinterest/publish`, payload, authHeaders());
        setPublishResult({ success: true, message: res.data.message, url: res.data.pin_url });
        fetchHistory();
      } else {
        if (!scheduleDate) {
          setPublishResult({ success: false, message: 'Select a date' });
          setPublishing(false);
          return;
        }
        const dt = new Date(scheduleDate);
        const [h, m] = scheduleTime.split(':');
        dt.setHours(parseInt(h), parseInt(m), 0, 0);
        const res = await axios.post(
          `${API_URL}/api/pinterest/schedule`,
          { ...payload, scheduled_at: dt.toISOString() },
          authHeaders()
        );
        setPublishResult({ success: true, message: res.data.message });
        fetchScheduled();
      }
    } catch (err) {
      const status = err.response?.status;
      let message = err.response?.data?.detail || 'Error ved publisering';
      if (status === 401) {
        message = 'Pinterest token expired. Please reconnect.';
        setTokenExpired(true);
        setConnected(false);
      }
      setPublishResult({ success: false, message });
    }
    setPublishing(false);
  };

  const handleCancelScheduled = async (scheduleId) => {
    try {
      await axios.delete(`${API_URL}/api/pinterest/scheduled/${scheduleId}`, authHeaders());
      fetchScheduled();
    } catch (err) { console.error('Cancel error:', err); }
  };

  // ─── NOT CONNECTED STATE ───
  if (!connected) {
    return (
      <div data-testid="pinterest-scheduler" className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-[#E60023] flex items-center justify-center">
            <Pin className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-sans font-bold text-stone-800 dark:text-stone-200 text-sm">Publiser til Pinterest</h3>
            <p className="font-sans text-stone-400 dark:text-stone-500 text-xs">Connect your account to publish and schedule</p>
          </div>
        </div>

        {!configured && (
          <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl p-3 mb-4" data-testid="not-configured-banner">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="font-sans text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
              <span className="font-semibold">Not configured:</span> Pinterest API keys have not been set up yet.
            </p>
          </div>
        )}

        {tokenExpired && (
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl p-3 mb-4" data-testid="token-expired-banner">
            <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="font-sans text-xs text-red-700 dark:text-red-400 leading-relaxed">
              <span className="font-semibold">Connection expired.</span> Please reconnect.
            </p>
          </div>
        )}

        <Button
          data-testid="connect-pinterest-btn"
          onClick={handleConnect}
          disabled={connecting || !configured}
          className="w-full bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl py-5 font-sans font-semibold disabled:opacity-50"
        >
          {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
          {tokenExpired ? 'Reconnect' : 'Connect Pinterest'}
        </Button>
        <p className="text-center text-xs font-sans text-stone-400 dark:text-stone-500 mt-2">Sikker OAuth-tilkobling via Pinterest</p>
      </div>
    );
  }

  // ─── CONNECTED STATE ───
  return (
    <div data-testid="pinterest-scheduler" className="space-y-4">
      {/* Connection status */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#E60023] flex items-center justify-center">
              <Pin className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <p className="font-sans font-semibold text-stone-800 dark:text-stone-200 text-xs">@{username}</p>
              <p className="font-sans text-green-600 dark:text-green-400 text-[10px] flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Tilkoblet
              </p>
            </div>
          </div>
          <button data-testid="disconnect-pinterest-btn" onClick={handleDisconnect} className="flex items-center gap-1 text-[10px] font-sans text-stone-400 hover:text-red-500 transition-colors">
            <Unlink className="w-3 h-3" /> Koble fra
          </button>
        </div>
      </div>

      {/* Boards loading/error */}
      {boardsLoading && (
        <div className="flex items-center justify-center gap-2 py-3">
          <Loader2 className="w-4 h-4 animate-spin text-[#E60023]" />
          <span className="font-sans text-xs text-stone-500 dark:text-stone-400">Henter boards...</span>
        </div>
      )}
      {boardsError && !boardsLoading && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-100 dark:border-red-800/30">
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="font-sans text-xs text-red-600 dark:text-red-400 flex-1">{boardsError}</p>
          <button onClick={fetchBoards} className="text-xs text-red-500 hover:text-red-700"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Pin Actions */}
      {boards.length > 0 && (
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-4">
          <h4 className="font-sans font-bold text-stone-800 dark:text-stone-200 text-xs mb-3">Publiser Pins</h4>
          <div className="space-y-2">
            {pins.map((pin, i) => (
              <div key={i} data-testid={`pin-action-row-${i}`} className="flex items-center gap-3 p-2 rounded-xl border border-stone-50 dark:border-stone-800 hover:border-stone-200 dark:hover:border-stone-700 transition-colors">
                <img src={pin.dataUrl} alt={`Pin ${i + 1}`} className="w-9 h-14 rounded-lg object-cover flex-shrink-0" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-medium text-stone-700 dark:text-stone-300 text-xs truncate">
                    {pinKits?.[i]?.title || `Pin ${i + 1}`}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                  <button data-testid={`publish-now-btn-${i}`} onClick={() => openModal('publish', i)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#E60023] text-white text-[10px] font-sans font-semibold hover:bg-[#cc001f] transition-colors">
                    <Send className="w-3 h-3" /> Publiser
                  </button>
                  <button data-testid={`schedule-btn-${i}`} onClick={() => openModal('schedule', i)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 text-[10px] font-sans font-semibold hover:border-[#E60023] hover:text-[#E60023] transition-colors">
                    <Clock className="w-3 h-3" /> Planlegg
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Pins */}
      {scheduledPins.length > 0 && (
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-4" data-testid="scheduled-pins-list">
          <h4 className="font-sans font-bold text-stone-800 dark:text-stone-200 text-xs mb-3 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-[#E60023]" /> Planlagt ({scheduledPins.length})
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {scheduledPins.map((sp) => (
              <div key={sp.id} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 dark:bg-stone-800">
                <div className="min-w-0 flex-1">
                  <p className="font-sans font-medium text-stone-700 dark:text-stone-300 text-xs truncate">{sp.title}</p>
                  <p className="font-sans text-stone-400 dark:text-stone-500 text-[10px]">{new Date(sp.scheduled_at).toLocaleString()} &middot; {sp.board_name || ''}</p>
                </div>
                <button data-testid={`cancel-scheduled-${sp.id}`} onClick={() => handleCancelScheduled(sp.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-stone-400 hover:text-red-500 transition-colors flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Published History */}
      {publishHistory.length > 0 && (
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-4" data-testid="publish-history-list">
          <h4 className="font-sans font-bold text-stone-800 dark:text-stone-200 text-xs mb-3 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Publisert ({publishHistory.length})
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {publishHistory.map((pp, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-green-50/50 dark:bg-green-900/10">
                <div className="min-w-0 flex-1">
                  <p className="font-sans font-medium text-stone-700 dark:text-stone-300 text-xs truncate">{pp.title}</p>
                  <p className="font-sans text-stone-400 dark:text-stone-500 text-[10px]">{new Date(pp.published_at).toLocaleString()}</p>
                </div>
                {pp.pin_url && (
                  <a href={pp.pin_url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 text-green-500 flex-shrink-0">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Publish/Schedule Modal with Smart Suggest ─── */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="publish-schedule-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">
              {modalMode === 'publish' ? 'Publiser til Pinterest' : 'Schedule Pin'}
            </DialogTitle>
            <DialogDescription className="font-sans text-xs text-stone-500">
              {modalMode === 'publish' ? 'The pin will be published immediately.' : 'Choose when the pin should be published.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Pin preview */}
            {pins[selectedPinIndex] && (
              <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
                <img src={pins[selectedPinIndex].dataUrl} alt="Pin preview" className="w-12 h-18 rounded-lg object-cover" style={{ width: '48px', height: '72px' }} draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-semibold text-stone-800 dark:text-stone-200 text-sm truncate">{editTitle || `Pin ${selectedPinIndex + 1}`}</p>
                  <p className="font-sans text-stone-400 text-xs truncate">{editDescription?.slice(0, 60) || 'None description'}...</p>
                </div>
              </div>
            )}

            {/* ─── AI Smart Suggest Button ─── */}
            <div className="relative">
              <button
                data-testid="smart-suggest-btn"
                onClick={() => fetchSmartSuggest(selectedPinIndex)}
                disabled={aiLoading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs font-sans font-semibold transition-all ${
                  aiApplied
                    ? 'bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/40 text-violet-700 dark:text-violet-300'
                    : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-600 hover:to-fuchsia-600 shadow-sm'
                }`}
              >
                {aiLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI analyserer...</>
                ) : aiApplied ? (
                  <><Sparkles className="w-3.5 h-3.5" /> AI-optimert &middot; Klikk for ny analyse</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5" /> Optimaliser med AI</>
                )}
              </button>
            </div>

            {/* AI Suggestion card */}
            {aiSuggestion && (
              <div className="bg-violet-50/50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-800/30 rounded-xl p-3 space-y-2" data-testid="ai-suggestion-card">
                <div className="flex items-center justify-between">
                  <p className="font-sans text-[10px] font-bold text-violet-600 dark:text-violet-400 uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI-forslag
                  </p>
                  <button onClick={restoreOriginal} className="flex items-center gap-1 text-[10px] font-sans text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors" data-testid="restore-original-btn">
                    <RotateCcw className="w-3 h-3" /> Backstill
                  </button>
                </div>

                {aiSuggestion.best_board && (
                  <div className="flex items-start gap-2">
                    <span className="font-sans text-[10px] font-semibold text-stone-500 dark:text-stone-400 mt-0.5 w-14 flex-shrink-0">Board:</span>
                    <div className="flex-1">
                      <p className="font-sans text-xs font-semibold text-stone-800 dark:text-stone-200">
                        {aiSuggestion.best_board.name}
                        {aiSuggestion.best_board.is_new && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-violet-100 dark:bg-violet-800/30 text-violet-600 dark:text-violet-400 text-[9px] rounded-full font-medium">Nytt board</span>
                        )}
                      </p>
                      <p className="font-sans text-[10px] text-stone-400 dark:text-stone-500">{aiSuggestion.best_board.reason}</p>
                    </div>
                  </div>
                )}

                {aiSuggestion.best_time && (
                  <div className="flex items-start gap-2">
                    <span className="font-sans text-[10px] font-semibold text-stone-500 dark:text-stone-400 mt-0.5 w-14 flex-shrink-0">Tid:</span>
                    <p className="font-sans text-xs text-stone-700 dark:text-stone-300">
                      {aiSuggestion.best_time.day} kl. {aiSuggestion.best_time.time} ({aiSuggestion.best_time.timezone})
                      <span className="text-stone-400 dark:text-stone-500 text-[10px] ml-1">— {aiSuggestion.best_time.tip}</span>
                    </p>
                  </div>
                )}

                {aiSuggestion.hashtags?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="font-sans text-[10px] font-semibold text-stone-500 dark:text-stone-400 mt-0.5 w-14 flex-shrink-0">Tags:</span>
                    <div className="flex flex-wrap gap-1">
                      {aiSuggestion.hashtags.map((tag, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-violet-100 dark:bg-violet-800/20 text-violet-600 dark:text-violet-400 text-[9px] rounded-full font-medium">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Editable Title */}
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">
                Tittel {aiApplied && <span className="text-violet-500 text-[10px] font-normal ml-1">AI-optimert</span>}
              </label>
              <input
                data-testid="pin-title-input"
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={100}
                className="w-full px-3 py-2.5 border border-stone-200 dark:border-stone-700 rounded-xl text-sm font-sans text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 focus:outline-none focus:border-violet-400 transition-colors"
              />
              <p className="font-sans text-[10px] text-stone-400 mt-1 text-right">{editTitle.length}/100</p>
            </div>

            {/* Editable Description */}
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">
                Description {aiApplied && <span className="text-violet-500 text-[10px] font-normal ml-1">AI-optimert</span>}
              </label>
              <textarea
                data-testid="pin-description-input"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2.5 border border-stone-200 dark:border-stone-700 rounded-xl text-sm font-sans text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 focus:outline-none focus:border-violet-400 transition-colors resize-none"
              />
              <p className="font-sans text-[10px] text-stone-400 mt-1 text-right">{editDescription.length}/500</p>
            </div>

            {/* Board selector */}
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">
                Pinterest Board {aiApplied && aiSuggestion?.best_board && <span className="text-violet-500 text-[10px] font-normal ml-1">AI-anbefalt</span>}
              </label>
              <div className="relative">
                <button data-testid="board-selector" onClick={() => setShowBoardDropdown(!showBoardDropdown)} className="w-full flex items-center justify-between px-3 py-2.5 border border-stone-200 dark:border-stone-700 rounded-xl text-sm font-sans text-stone-700 dark:text-stone-300 hover:border-stone-300 dark:hover:border-stone-600 bg-white dark:bg-stone-800 transition-colors">
                  {selectedBoardName || 'Select a board'}
                  <ChevronDown className="w-4 h-4 text-stone-400" />
                </button>
                {showBoardDropdown && (
                  <div className="absolute w-full bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl shadow-lg z-50 mt-1 max-h-48 overflow-y-auto">
                    {boards.map(b => (
                      <button key={b.id} data-testid={`board-option-${b.id}`} onClick={() => { setSelectedBoard(b.id); setSelectedBoardName(b.name); setShowBoardDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-xs font-sans hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors ${b.id === selectedBoard ? 'text-[#E60023] font-semibold bg-red-50/50 dark:bg-red-900/20' : 'text-stone-600 dark:text-stone-400'}`}
                      >
                        {b.name} <span className="text-stone-400 dark:text-stone-500">({b.pin_count} pins)</span>
                        {aiSuggestion?.best_board?.id === b.id && <span className="ml-1.5 text-violet-500 text-[9px]">AI-anbefalt</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Schedule date/time */}
            {modalMode === 'schedule' && (
              <div className="space-y-3">
                <div>
                  <label className="font-sans text-xs font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">
                    <CalendarIcon className="w-3.5 h-3.5 inline mr-1" /> Date
                  </label>
                  <div className="border border-stone-200 dark:border-stone-700 rounded-xl overflow-hidden" data-testid="schedule-calendar">
                    <Calendar mode="single" selected={scheduleDate} onSelect={setScheduleDate} disabled={(date) => date < new Date()} className="rounded-xl" />
                  </div>
                </div>
                <div>
                  <label className="font-sans text-xs font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">
                    <Clock className="w-3.5 h-3.5 inline mr-1" /> Time
                    {aiSuggestion?.best_time && (
                      <span className="text-violet-500 text-[10px] font-normal ml-1">AI anbefaler {aiSuggestion.best_time.day} {aiSuggestion.best_time.time}</span>
                    )}
                  </label>
                  <input data-testid="schedule-time-input" type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full px-3 py-2.5 border border-stone-200 dark:border-stone-700 rounded-xl text-sm font-sans text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 focus:outline-none focus:border-[#E60023] transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Result message */}
            {publishResult && (
              <div data-testid="publish-result" className={`flex items-start gap-2 p-3 rounded-xl ${publishResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30' : 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30'}`}>
                {publishResult.success ? <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                <div>
                  <p className={`font-sans text-xs font-medium ${publishResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>{publishResult.message}</p>
                  {publishResult.url && (
                    <a href={publishResult.url} target="_blank" rel="noopener noreferrer" className="font-sans text-[10px] text-green-600 dark:text-green-400 underline flex items-center gap-1 mt-1">
                      View on Pinterest <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Action button */}
            {!publishResult?.success && (
              <Button data-testid="confirm-publish-btn" onClick={handleSubmit} disabled={publishing || !selectedBoard}
                className={`w-full rounded-xl py-5 font-sans font-semibold ${modalMode === 'publish' ? 'bg-[#E60023] hover:bg-[#cc001f] text-white' : 'bg-stone-900 hover:bg-stone-800 text-white dark:bg-stone-200 dark:hover:bg-stone-300 dark:text-stone-900'}`}
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : modalMode === 'publish' ? <Send className="w-4 h-4 mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
                {modalMode === 'publish' ? 'Publish now' : 'Schedule Pin'}
              </Button>
            )}

            {publishResult?.success && (
              <Button onClick={() => setShowModal(false)} variant="outline" className="w-full rounded-xl py-5 font-sans font-semibold">Done</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PinterestScheduler;
